import type { ApiClient } from './client';
import type { BiltEvent } from '../../types/event';
import type {
  EventJudge,
  EventResultsPayload,
  JudgeEventDashboard,
  JudgeScore,
  PublishAwardInput,
  VehicleAward,
} from '../../types/judging';

export const judgingMethods = {
  async searchJudgeUsers(
    query: string,
    opts?: { eventId?: number; limit?: number }
  ): Promise<
    Array<{ id: number; display_name: string; username: string; role: string }>
  > {
    const q = query.trim();
    if (q.length < 2) return [];
    const params = new URLSearchParams({
      q,
      limit: String(opts?.limit ?? 10),
    });
    if (opts?.eventId && opts.eventId > 0) {
      params.set('event_id', String(opts.eventId));
    }
    const response = await this.makeRequest<
      Array<{ id: number; display_name: string; username: string; role: string }>
    >(`/judge_user_search.php?${params.toString()}`, { method: 'GET' });
    return response.success && Array.isArray(response.data) ? response.data : [];
  },

  async addEventJudge(
    eventId: number,
    username: string,
    classIds?: number[],
    userId?: number
  ): Promise<EventJudge> {
    const body: Record<string, unknown> = { event_id: eventId };
    if (userId && userId > 0) {
      body.user_id = userId;
    } else {
      body.username = username;
    }
    if (classIds !== undefined) {
      body.class_ids = classIds;
    }
    const response = await this.makeRequest<EventJudge>('/event_judge_add.php', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (response.success && response.data) return response.data as EventJudge;
    throw new Error(response.error || 'Failed to add judge');
  },

  async updateEventJudgeClasses(assignmentId: number, classIds: number[]): Promise<{
    id: number;
    event_id: number;
    user_id: number;
    class_ids: number[];
  }> {
    const response = await this.makeRequest<{
      id: number;
      event_id: number;
      user_id: number;
      class_ids: number[];
    }>('/event_judge_classes_update.php', {
      method: 'POST',
      body: JSON.stringify({ id: assignmentId, class_ids: classIds }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to update judge classes');
  },

  async getEventJudges(eventId: number): Promise<EventJudge[]> {
    const response = await this.makeRequest<EventJudge[]>(
      `/event_judges_list.php?event_id=${eventId}`,
      { method: 'GET' }
    );
    return response.success && Array.isArray(response.data) ? response.data : [];
  },

  async removeEventJudge(judgeAssignmentId: number): Promise<void> {
    const response = await this.makeRequest('/event_judge_remove.php', {
      method: 'POST',
      body: JSON.stringify({ id: judgeAssignmentId }),
    });
    if (!response.success) throw new Error(response.error || 'Failed to remove judge');
  },

  async getJudgeEvents(): Promise<BiltEvent[]> {
    const response = await this.makeRequest<BiltEvent[]>('/judge_events_list.php', { method: 'GET' });
    if (response.success && Array.isArray(response.data)) return response.data;
    return [];
  },

  async getJudgeRegistrations(eventId: number): Promise<JudgeEventDashboard> {
    const response = await this.makeRequest<JudgeEventDashboard>(
      `/judge_registrations_list.php?event_id=${eventId}`,
      { method: 'GET' }
    );
    if (response.success && response.data && typeof response.data === 'object') {
      const data = response.data as JudgeEventDashboard;
      return {
        event: data.event ?? null,
        assigned_class_ids: Array.isArray(data.assigned_class_ids) ? data.assigned_class_ids : [],
        classes: Array.isArray(data.classes) ? data.classes : [],
        registrations: Array.isArray(data.registrations) ? data.registrations : [],
        progress: data.progress ?? {
          total: 0,
          finalized: 0,
          drafts: 0,
          remaining: 0,
          not_started: 0,
          percent: 0,
        },
      };
    }
    throw new Error(response.error || 'Failed to load judging list');
  },

  async getJudgeScore(registrationId: number): Promise<JudgeScore | null> {
    const response = await this.makeRequest<JudgeScore | null>(
      `/judge_score_get.php?registration_id=${registrationId}`,
      { method: 'GET' }
    );
    if (response.success) return response.data ?? null;
    throw new Error(response.error || 'Failed to load score');
  },

  async getJudgeBuildSheet(registrationId: number): Promise<{
    registration_id: number;
    vehicle: {
      id: number;
      year?: number | null;
      make?: string | null;
      model?: string | null;
      trim?: string | null;
      nickname?: string | null;
      description?: string | null;
      engine?: string | null;
      transmission?: string | null;
      drivetrain?: string | null;
      chassis?: string | null;
      public_slug?: string | null;
      is_public?: boolean;
    };
    owner_username?: string;
    modifications: Array<Record<string, unknown>>;
    images: Array<{ image_url?: string; caption?: string | null; title?: string | null; id?: number }>;
  }> {
    const response = await this.makeRequest<any>(
      `/judge_build_sheet.php?registration_id=${registrationId}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to load build sheet');
  },

  async saveJudgeScore(payload: {
    registration_id: number;
    score_exterior?: number | null;
    score_interior?: number | null;
    score_engine?: number | null;
    score_execution?: number | null;
    score_creativity?: number | null;
    is_finalized?: boolean;
    notes?: string;
  }): Promise<JudgeScore> {
    const response = await this.makeRequest<JudgeScore>('/judge_score_save.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to save score');
  },

  async unlockJudgeScore(opts: {
    score_id?: number;
    registration_id?: number;
    judge_user_id?: number;
  }): Promise<JudgeScore> {
    const response = await this.makeRequest<JudgeScore>('/judge_score_unlock.php', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to unlock score');
  },

  async getEventResults(eventId: number): Promise<EventResultsPayload> {
    const response = await this.makeRequest<EventResultsPayload | unknown>(
      `/event_results.php?event_id=${eventId}`,
      { method: 'GET' }
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to load results');
    }
    const data = response.data;
    // Backward compat: older servers returned a bare results array
    if (Array.isArray(data)) {
      return {
        results: data,
        class_progress: [],
        judge_assigned_count: 0,
        formula: '',
      };
    }
    if (data && typeof data === 'object' && Array.isArray((data as EventResultsPayload).results)) {
      const payload = data as EventResultsPayload;
      return {
        results: payload.results,
        class_progress: Array.isArray(payload.class_progress) ? payload.class_progress : [],
        judge_assigned_count: payload.judge_assigned_count ?? 0,
        formula: payload.formula ?? '',
      };
    }
    return {
      results: [],
      class_progress: [],
      judge_assigned_count: 0,
      formula: '',
    };
  },

  async getEventAwards(eventId: number): Promise<VehicleAward[]> {
    const response = await this.makeRequest<VehicleAward[]>(
      `/event_awards_list.php?event_id=${eventId}`,
      { method: 'GET' }
    );
    return response.success && Array.isArray(response.data) ? response.data : [];
  },

  async publishAwards(
    eventId: number,
    awards: PublishAwardInput[],
    opts?: { forceIncomplete?: boolean }
  ): Promise<VehicleAward[]> {
    try {
      const response = await this.makeRequest<VehicleAward[]>('/awards_publish.php', {
        method: 'POST',
        body: JSON.stringify({
          event_id: eventId,
          awards,
          force_incomplete: opts?.forceIncomplete ? 1 : undefined,
        }),
      });
      if (response.success && Array.isArray(response.data)) return response.data;
      throw Object.assign(new Error(response.error || 'Failed to publish awards'), {
        code: response.code,
        incomplete_classes: response.incomplete_classes,
      });
    } catch (err: any) {
      if (err?.code === 'incomplete_class' || err?.incomplete_classes) {
        throw err;
      }
      throw err instanceof Error ? err : new Error(String(err));
    }
  },
} satisfies ThisType<ApiClient>;
